import { extractSegmentPoints } from "./mapCoordinatesUtils";

/**
 * Build a LineString between a segment's departure and arrival.
 * Returns a small handle with remove() so you can clean up later.
 *
 * @param {Object} opts
 * @param {maplibregl.Map} opts.map
 * @param {Object}         opts.segment           - your itinerary segment (must have coordinates.departure/arrival)
 * @param {string}        [opts.color="#2563eb"]  - CSS color or rgba()
 * @param {number}        [opts.width=3]         - line width in pixels
 * @param {number}        [opts.opacity=0.9]
 * @param {number[]}      [opts.dasharray]       - e.g., [2, 2] for dashed (optional)
 * @returns {{ id: string, remove: Function }}   - handle to remove source+layer
 */
export function createTransportLine({
  map,
  segment,
  color = "#2563eb", // Tailwind blue-600-ish
  width = 3,
  opacity = 0.9,
  dasharray, // e.g. [2, 1]
}) {
  if (!map || !segment) return { id: "", remove: () => {} };

  const idBase = String(
    segment.id ??
      segment.uuid ??
      segment._id ??
      Math.random().toString(36).slice(2)
  );
  const sourceId = `segline-src:${idBase}`;
  const layerId = `segline-lyr:${idBase}`;

  // Get departure/arrival
  const { departure, arrival } = extractSegmentPoints(segment);
  if (!departure || !arrival) {
    // nothing to draw
    return { id: idBase, remove: () => {} };
  }

  const line = {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates: [
        [departure.lng, departure.lat],
        [arrival.lng, arrival.lat],
      ],
    },
    properties: {
      segmentId: idBase,
      type: (segment.type || "").toLowerCase(),
      mode: (segment.mode || "").toLowerCase(),
      title: segment.title || segment.name || "",
    },
  };

  // (Re)create source safely
  const prior = map.getSource(sourceId);
  if (prior) {
    try {
      prior.setData(line);
    } catch {}
  } else {
    map.addSource(sourceId, { type: "geojson", data: line });
  }

  // Add layer if missing
  if (!map.getLayer(layerId)) {
    map.addLayer({
      id: layerId,
      type: "line",
      source: sourceId,
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": color,
        "line-width": width,
        "line-opacity": opacity,
        ...(Array.isArray(dasharray) ? { "line-dasharray": dasharray } : {}),
      },
    });
  }

  // Return a handle to remove it
  const remove = () => {
    try {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
    } catch {}
    try {
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    } catch {}
  };

  return { id: idBase, remove };
}

/**
 * Convenience: sync lines for a set of segments.
 * - If `visible` is false, removes all known lines.
 * - Otherwise creates/updates for transportation segments that have dep+arr.
 *
 * @param {Object} opts
 * @param {maplibregl.Map} opts.map
 * @param {Array}          opts.segments
 * @param {boolean}        opts.visible
 * @param {Object}        [opts.style]       - { color, width, opacity, dasharray }
 * @param {Map}           [opts.registry]    - Map<string, {remove:Function}>
 * @returns {Map} updated registry
 */
export function syncTransportLines({
  map,
  segments = [],
  visible,
  style = {},
  registry = new Map(),
}) {
  // Remove all if hidden
  if (!visible) {
    registry.forEach((h) => {
      try {
        h.remove();
      } catch {}
    });
    registry.clear();
    return registry;
  }

  const byId = new Set();

  for (const seg of segments) {
    const type = (seg.type || "").toLowerCase();
    if (type !== "transportation") continue;

    const id = String(seg.id ?? seg.uuid ?? seg._id ?? "");
    if (!id) continue;

    byId.add(id);

    // Already created? nothing to do (you can extend to update color here if you want)
    if (registry.has(id)) continue;

    // Create only if both dep+arr exist
    const { departure, arrival } = extractSegmentPoints(seg);
    if (!departure || !arrival) continue;

    const h = createTransportLine({
      map,
      segment: seg,
      color: style.color,
      width: style.width,
      opacity: style.opacity,
      dasharray: style.dasharray,
    });
    // Only store if we actually added (handle may be empty if no points)
    if (h && h.remove) registry.set(id, h);
  }

  // Remove any leftover lines for segments no longer present
  for (const [id, h] of registry.entries()) {
    if (!byId.has(id)) {
      try {
        h.remove();
      } catch {}
      registry.delete(id);
    }
  }

  return registry;
}
